import { useSelector } from 'react-redux';
import { get } from 'lodash';
import { useCallback } from 'react';
import { Log } from 'js/components/data_flows/cards/FlowRunLogs/types';

export interface MetricsState {
  [key: string]: any;
}

export interface RootState {
  metrics: MetricsState;
}

export type FlowRunLogsWithFacetsType = {
  logs: Record<string, Log[]>;
  total: number;
  size?: number;
  hasMore: boolean | undefined;
  nextPageValues: [number, string] | 'initial_request' | null;
  facets: Record<string, any>;
  isLoading: boolean;
  error: any | null;
};

interface UseMetricsRepository {
  data: MetricsState;
  getRunsMetrics: () => {
    error: boolean;
    data: any[];
    loading: boolean;
    lastUpdated: number | null;
    flowIds: number[] | string[];
    metricsMappedByOriginNodeId: Record<string, any>;
    meta: {
      currentPage: number;
      totalCount: number;
      pageCount: number;
    };
  };
  getFlowRunSummary: () => Record<string, any>;
  getMetricById: (metricId: string) => any;
  getFileDetailsByFileName: (
    fileName: string,
  ) => undefined | { error: boolean; data: any[]; loading: boolean };
  getFlowRunLogs: (
    nodeId: string | number,
    runId: number,
  ) => undefined | { data: any[]; [key: string]: any };
  getFlowRunLogsWithFacets: () => FlowRunLogsWithFacetsType | undefined;
}

const runsSelector = (s: any) => s.metrics.runs;

function useMetricsRepository(path: string = ''): UseMetricsRepository {
  const data = useSelector((state: RootState) => (path ? get(state.metrics, path) : state.metrics));

  const allRunsMetrics = useSelector(runsSelector);

  function getMetricById(metricId: string) {
    return (data as MetricsState)?.[metricId];
  }

  function getFileDetailsByFileName(fileName: string) {
    const details = getMetricById('fileDetails');
    return details?.[fileName];
  }

  const getRunsMetrics = useCallback(() => allRunsMetrics, [allRunsMetrics]);

  const getFlowRunSummary = useCallback(() => {
    return data?.flowRunSummary as Record<string, any>;
  }, [data]);

  function getFlowRunLogs(nodeId: string | number, runId: number) {
    return data?.flowRunLogs?.[nodeId]?.[runId];
  }

  function getFlowRunLogsWithFacets() {
    return data?.flowRunLogsWithFacets as FlowRunLogsWithFacetsType | undefined;
  }

  return {
    data,
    getRunsMetrics,
    getMetricById,
    getFlowRunSummary,
    getFileDetailsByFileName,
    getFlowRunLogs,
    getFlowRunLogsWithFacets,
  };
}

export { useMetricsRepository };
